import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

export interface CancelInvocationsByDatasetParams {
  datasetId: string;
}

/**
 * Cancel all active (queued, scheduled, downloading, processing, uploading) invocations for a dataset.
 */
export function useCancelInvocationsByDataset() {
  const { datasetService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<undefined, Error, CancelInvocationsByDatasetParams>({
    mutationFn: async ({ datasetId }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("Requires login"));
      }

      let hasMore = true;

      const MAX_ITERATIONS = 100; // Arbitrary limit to prevent infinite loop
      let it = 0;

      while (hasMore && it < MAX_ITERATIONS) {
        const response = await datasetService.cancelAssociatedInvocations(
          datasetId,
          currentOrganization.org_id,
        );

        hasMore = response.has_more;
        it += 1;
      }
    },
    onSuccess: (_, { datasetId }) => {
      // Invalidate active invocations count query (used, e.g, by the 'Cancel All' button) to refresh the UI
      queryClient
        .invalidateQueries({
          queryKey: ["activeInvocationCount", datasetId],
        })
        .catch(() => {
          /** swallow */
        });
    },
  });
}
